import { useState, useRef, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import type { PlayerImportResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

// CSV import page -- upload file, show results

export default function PlayerImportPage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PlayerImportResponse | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!schoolId) return;

    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    setError(null);
    setResult(null);
    setSubmitting(true);

    try {
      const res = await api.importPlayers(schoolId, file);
      setResult(res);
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Import failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="player-import" data-state={submitting ? "submitting" : error ? "error" : result ? "complete" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Import Players</h1>
        <p className="text-muted-foreground">
          Upload a CSV file to bulk-create players
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">CSV Upload</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="player-import">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label>Expected Format</Label>
              <pre className="overflow-x-auto rounded bg-muted p-3 text-xs text-muted-foreground">
{`first_name,surname,date_of_birth,family_code
John,Smith,2010-03-15,SMITH01
Jane,Smith,2012-07-22,SMITH01
Bob,Jones,2011-01-08,JONES02`}
              </pre>
              <p className="text-xs text-muted-foreground">
                Family code is optional. If provided, use the code from the school
                admin system. Siblings share the same code.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="csv-file">CSV File</Label>
              <input
                id="csv-file"
                data-field="csv-file"
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                aria-label="CSV File"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Importing..." : "Import"}
              </Button>
              <Link
                to="/players"
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>

          {result && (
            <div className="mt-4 flex flex-col gap-2 rounded border border-border p-4">
              <p className="font-medium">
                {result.imported} player{result.imported !== 1 ? "s" : ""}{" "}
                imported
              </p>
              {result.errors.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-destructive">
                    {result.errors.length} error
                    {result.errors.length !== 1 ? "s" : ""}:
                  </p>
                  <ul className="list-inside list-disc text-sm text-destructive">
                    {result.errors.map((err, i) => (
                      <li key={i}>
                        Row {err.row}: {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.errors.length === 0 && (
                <Link
                  to="/players"
                  className={buttonVariants({
                    variant: "outline",
                    size: "sm",
                  })}
                >
                  View Players
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
